import { RiArrowDownSLine } from '@remixicon/react';
import { Dialog, DialogTrigger } from 'react-aria-components';
import { AgButton, type AgButtonProps } from './AgButton';
import { AgPopover } from './AgPopover';
import { cn } from './utils';

type ButtonVariantProps = Pick<AgButtonProps, 'variant' | 'size' | 'shape'>;

export interface AgPopoverTriggerProps extends ButtonVariantProps {
  /** Render prop receiving close function for popover content */
  children: (close: () => void) => React.ReactNode;
  /** Content for the trigger button (label text, etc.) */
  trigger?: React.ReactNode;
  /** Icon in trigger button */
  icon?: React.ReactNode;
  /** Show chevron indicator on trigger */
  showChevron?: boolean;
  /** Additional classes on trigger button */
  className?: string;
  /** Additional classes on the popover container */
  popoverClassName?: string;
  /** Disable the trigger button */
  isDisabled?: boolean;
  /** Horizontal alignment of the popover */
  align?: 'start' | 'end';
  /** Test id forwarded to the trigger button */
  'data-testid'?: string;
}

export function AgPopoverTrigger({
  children,
  trigger,
  icon,
  showChevron = false,
  variant = 'ghost',
  size = 'md',
  shape = 'rounded',
  className,
  popoverClassName,
  isDisabled,
  align = 'start',
  'data-testid': dataTestId,
}: AgPopoverTriggerProps) {
  const isIconOnly = size?.toString().startsWith('icon-');

  return (
    <DialogTrigger>
      <AgButton
        variant={variant}
        size={size}
        shape={shape}
        className={cn('data-pressed:border-brand-focus', className)}
        isDisabled={isDisabled}
        data-testid={dataTestId}
      >
        {icon && !isIconOnly && <span className="shrink-0">{icon}</span>}
        {isIconOnly ? icon : trigger}
        {showChevron && !isIconOnly && <RiArrowDownSLine className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />}
      </AgButton>
      <AgPopover placement={align === 'end' ? 'bottom end' : 'bottom start'} className={popoverClassName}>
        <Dialog className="outline-none">{({ close }) => children(close)}</Dialog>
      </AgPopover>
    </DialogTrigger>
  );
}
